import { Button } from "@/components/ui/button";
import { Star, MapPin, Clock, Phone, ChefHat, Utensils, Wine, CalendarDays } from "lucide-react";
import EditableText from "./EditableText";
import type { WebsiteCustomizations } from "./PremiumTemplate";

interface Props {
  name: string; desc: string; product: string; city: string;
  customizations?: WebsiteCustomizations;
  editMode?: boolean;
  onCustomize?: (field: keyof WebsiteCustomizations, value: string) => void;
}

const menu = [
  { cat: "Entrées", items: [
    { name: "Tartare de saumon", desc: "Avocat, agrumes, sésame torréfié", price: "16 EUR" },
    { name: "Velouté de saison", desc: "Légumes du marché, crème truffe", price: "12 EUR" },
    { name: "Burrata crémeuse", desc: "Tomates confites, pesto basilic", price: "14 EUR" },
  ]},
  { cat: "Plats", items: [
    { name: "Filet de boeuf", desc: "Sauce au poivre, gratin dauphinois", price: "32 EUR" },
    { name: "Risotto aux cèpes", desc: "Parmesan 24 mois, huile de truffe", price: "26 EUR" },
    { name: "Pavé de bar", desc: "Légumes croquants, beurre blanc", price: "28 EUR" },
  ]},
  { cat: "Desserts", items: [
    { name: "Fondant au chocolat", desc: "Coeur coulant, glace vanille", price: "12 EUR" },
    { name: "Crème brûlée", desc: "Vanille de Madagascar", price: "10 EUR" },
  ]},
];

const RestaurantTemplate = ({ name, desc, product, city, customizations, editMode = false, onCustomize }: Props) => {
  const c = customizations || {};
  const edit = (field: keyof WebsiteCustomizations) => (v: string) => onCustomize?.(field, v);
  const displayName = c.heroTitle || name;
  const displayDesc = c.heroSubtitle || desc;
  const displayPhone = c.phone || "01 23 45 67 89";
  return (
  <div className="bg-[#1c1917] text-[#fef3c7] min-h-screen">
    {/* Nav */}
    <nav className="fixed top-0 w-full z-50 bg-[#1c1917]/90 backdrop-blur-xl border-b border-orange-500/10">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ChefHat className="h-5 w-5 text-orange-500" />
          <EditableText as="span" value={displayName} onChange={edit("heroTitle")} editMode={editMode} className="text-lg font-bold" data-field="brand-name" />
        </div>
        <div className="hidden md:flex items-center gap-8 text-sm text-[#fef3c7]/60">
          <span className="hover:text-orange-400 cursor-pointer">La carte</span>
          <span className="hover:text-orange-400 cursor-pointer">Notre histoire</span>
          <span className="hover:text-orange-400 cursor-pointer">Galerie</span>
          <Button className="bg-orange-600 hover:bg-orange-700 text-white h-9 px-5 rounded-full text-sm">Réserver</Button>
        </div>
      </div>
    </nav>

    {/* Hero */}
    <section className="min-h-screen flex items-center pt-16 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-orange-500/5 via-transparent to-transparent" />
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `radial-gradient(circle, #f97316 1px, transparent 1px)`,
        backgroundSize: "40px 40px"
      }} />
      <div className="relative max-w-7xl mx-auto px-6 py-20 text-center space-y-8">
        <span className="inline-flex items-center gap-2 text-orange-400 text-sm font-medium">
          <Star className="h-4 w-4 fill-orange-400" /> Cuisine gastronomique
        </span>
        <h1 className="text-6xl md:text-8xl font-bold leading-[0.9]">
          <EditableText as="span" value={displayName} onChange={edit("heroTitle")} editMode={editMode} data-field="hero-title" />
        </h1>
        <EditableText as="p" value={displayDesc} onChange={edit("heroSubtitle")} editMode={editMode} multiline className="text-xl text-[#fef3c7]/60 max-w-lg mx-auto" data-field="hero-subtitle" />
        <div className="flex justify-center gap-4 pt-4">
          <Button className="bg-orange-600 hover:bg-orange-700 text-white h-14 px-8 rounded-full text-base">
            <CalendarDays className="mr-2 h-5 w-5" /> Réserver une table
          </Button>
          <Button variant="outline" className="h-14 px-8 rounded-full border-orange-500/30 text-orange-400 hover:bg-orange-500/10">
            Voir la carte
          </Button>
        </div>
        <div className="flex justify-center gap-8 pt-8 text-sm text-[#fef3c7]/50">
          <span className="flex items-center gap-2"><MapPin className="h-4 w-4 text-orange-500" />{city}</span>
          <span className="flex items-center gap-2"><Clock className="h-4 w-4 text-orange-500" />12h-14h / 19h-22h</span>
          <span className="flex items-center gap-2"><Phone className="h-4 w-4 text-orange-500" /><EditableText as="span" value={displayPhone} onChange={edit("phone")} editMode={editMode} data-field="phone" /></span>
        </div>
      </div>
    </section>

    {/* Menu */}
    <section className="py-24 bg-[#fef3c7] text-[#1c1917]">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-16">
          <Utensils className="h-8 w-8 text-orange-600 mx-auto mb-4" />
          <h2 className="text-4xl md:text-5xl font-bold">La Carte</h2>
          <p className="text-gray-600 mt-2">Des produits frais, une cuisine d'exception</p>
        </div>
        {menu.map((section) => (
          <div key={section.cat} className="mb-12">
            <h3 className="text-2xl font-bold text-orange-700 mb-6 pb-3 border-b-2 border-orange-200">{section.cat}</h3>
            <div className="space-y-6">
              {section.items.map((item) => (
                <div key={item.name} className="flex justify-between items-start">
                  <div>
                    <h4 className="text-lg font-semibold">{item.name}</h4>
                    <p className="text-sm text-gray-500">{item.desc}</p>
                  </div>
                  <span className="text-lg font-bold text-orange-600 shrink-0 ml-4">{item.price}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>

    {/* CTA */}
    <section className="py-24 bg-orange-600 text-white text-center">
      <div className="max-w-3xl mx-auto px-6 space-y-6">
        <Wine className="h-10 w-10 mx-auto opacity-80" />
        <h2 className="text-4xl md:text-5xl font-bold">Une expérience inoubliable vous attend</h2>
        <p className="text-orange-100 text-lg">Réservez votre table et laissez-vous surprendre par notre chef.</p>
        <Button className="bg-white text-orange-700 hover:bg-orange-50 h-14 px-10 rounded-full text-base font-semibold">
          Réserver maintenant
        </Button>
      </div>
    </section>

    {/* Footer */}
    <footer className="py-12 border-t border-orange-500/10">
      <div className="max-w-7xl mx-auto px-6 text-center space-y-2">
        <p className="text-lg font-bold text-orange-400">{name}</p>
        <p className="text-sm text-[#fef3c7]/40">{city} — © 2026 — Propulsé par Grafton IA</p>
      </div>
    </footer>
  </div>
  );
};

export default RestaurantTemplate;
